import React, { useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import Honeybadger from 'honeybadger-js';

import {
  PagesQuery,
  usePagesQuery,
  useRegistrySummaryQuery,
  usePrintPromotionMutation,
  PagesFragment,
  Wws_Page,
} from '../../generated/graphql';
import registryInfo from '../../helpers/registryInfo';
import { addPageRoute, newItemsRoute } from '../../helpers/routes';
import { TrackingContext } from '../../TrackingContext';
import useIsLiteSite from '../../helpers/useIsLiteSite';
import HelpfulIdeasSection from '../HelpfulIdeasSection';
import styles from '../Dashboard/dashboard.scss';
import LiteHomePageCard from '../LiteHomePageCard';
import AddPageButton from '../ManagePagesRail/AddPageButton';
import PageCard from '../PageCard';
import { PageItemsRail } from '../PageItemsRail';
import { PageSettingsRail } from '../PageSettingsRail';
import { RegistryPageCard } from '../RegistryPageCard';
import RsvpPageCard from '../RsvpPageCard';
import { useWorkflowUnstartedState, useSeeWorkflow } from '../RsvpPageCard/utils';
import { WeddingPageCard } from '../WeddingPageCard';
import PageSectionHeader from '../PageSectionHeader';
import { BasicInfoRail } from '../BasicInfoRail';
import { PageCardUIProvider } from '../../PageCardUIContext';
import { ManagePagesRail } from '../ManagePagesRail';
import DressCodePageCard from '../DressCodePageCard';

import {
  useWWSRegistryGuidanceModule,
  VARIANT as REGISTRY_GUIDANCE_VARIANT,
} from '../../hooks/experiment/useWWSRegistryGuidanceModule';
import { useWebsiteLayoutContext } from '../../context/WebsiteLayoutContext';
import { useAiTextGenerationContext } from '../../context/AiTextGenerationContext';
import { TextGenerationModal } from '../Modals/TextGenerationModal';

type Page = PagesQuery['pages'][0];

const getHelpfulIdeasPages = (pages: Page[], hasAnyRegistry: boolean) =>
  (pages || []).filter(
    page => (!page?.show && !page?.items?.length) || (page?.type === 'RegistryPage' && !hasAnyRegistry)
  );

const LeftContainer: React.FC = () => {
  const liteSite = useIsLiteSite();
  const { data } = usePagesQuery();
  const { currentLayout } = useWebsiteLayoutContext();
  const { data: registryData } = useRegistrySummaryQuery();
  const history = useHistory();
  const { track } = useContext(TrackingContext);
  const seeWorkflow = useSeeWorkflow();
  const [scrollToView, setScrollToView] = useState(false);

  const { isUnstartedState } = useWorkflowUnstartedState();
  const goToWorkflow = !!(seeWorkflow && isUnstartedState);

  const { variant: registryGuidanceVariant, loading: registryGuidanceExpLoading } = useWWSRegistryGuidanceModule();
  const showRegistryGuidanceVariant = registryGuidanceVariant === REGISTRY_GUIDANCE_VARIANT;

  const isSinglePageSite = currentLayout === 'single-page';
  const pageOrSection = isSinglePageSite ? 'Section' : 'Page';
  // liteSite users see a separate guidance card
  // standard users see the guidance card in place
  // when started see rsvp card
  const standardSeeWorkflow = !liteSite && isUnstartedState;
  const liteSeeRsvpCard = liteSite && !isUnstartedState;
  const standardSeeRsvpCard = !liteSite && !isUnstartedState;
  const seeRsvpGuidanceInPlace = standardSeeWorkflow || liteSeeRsvpCard;
  const seeRsvpCard = !!seeWorkflow && (seeRsvpGuidanceInPlace || standardSeeRsvpCard);

  const { hasAnyRegistry } = registryInfo(registryData?.registrySummary);
  const pages = data?.pages;
  const helpfulIdeasPages = useMemo(() => getHelpfulIdeasPages(pages as Page[], hasAnyRegistry), [
    pages,
    hasAnyRegistry,
  ]);

  const dressCodePage = pages?.find(page => page.type === 'DressCodePage' || page.title === 'Dress Code');

  const shouldShowDressCodeGuidance = !liteSite && !dressCodePage;

  const handleCreatedPage = useCallback(
    (page: PagesFragment | Wws_Page) => {
      try {
        if (page?.id) {
          history.push(newItemsRoute(page.id));
        }
      } catch (e) {
        Honeybadger.notify(e instanceof Error ? e : new Error('Unknown error in handleCreatedPage'), {
          context: { component: 'LeftContainer', action: 'handle-created-page', caught: e },
        });
      }
    },
    [history]
  );

  const {
    modalOpen: textGenerationModalOpen,
    isThinking,
    errorMessage,
    isRetryableError,
    selectedOption,
    presetOptions,
    retryRequest,
    setModalOpen,
    generatedTextHistory,
    field,
    isModalInZeroState,
    activeHistoryPage,
  } = useAiTextGenerationContext();
  const getGeneratedText = useCallback((): string => {
    if (isModalInZeroState) {
      return field?.fieldText || '';
    }

    return generatedTextHistory && generatedTextHistory.length > 0
      ? generatedTextHistory[activeHistoryPage].generatedText
      : '';
  }, [generatedTextHistory, field, isModalInZeroState, activeHistoryPage]);

  const getGeneratedTextModalTitle = useCallback(
    (selectedOption: string) => {
      if (isModalInZeroState) {
        return 'Custom Prompt';
      }
      //don't use the history if no history exists or we're on the latest page
      const presetOptionToLabelMap =
        isThinking || !generatedTextHistory || generatedTextHistory.length === activeHistoryPage
          ? selectedOption
          : generatedTextHistory?.[activeHistoryPage]?.presetOption;
      return presetOptions.find(option => option.value === presetOptionToLabelMap)?.label || 'Custom Prompt';
    },
    [presetOptions, isModalInZeroState, generatedTextHistory, activeHistoryPage, isThinking]
  );
  const generatedText = getGeneratedText();

  const generatedTextModalTitle = getGeneratedTextModalTitle(selectedOption || '');

  const textGenerationPrimaryClick = () => {
    field?.setFieldText?.(field?.fieldName || '', generatedText);
    track('AI Button Clicked', {
      action: 'WWS AI',
      actionName: 'text replaced',
    });
    setModalOpen(false);
  };

  const textGenerationSecondaryClick = () => {
    field?.setFieldText?.(field?.fieldName || '', `${field?.fieldText} ${generatedText}`);
    track('AI Button Clicked', {
      action: 'WWS AI',
      actionName: 'text insert',
    });
    setModalOpen(false);
  };

  const [promotionLoading, setPromotionLoading] = useState(true);

  const [printPromotion, { data: printPromotionData }] = usePrintPromotionMutation();
  useEffect(() => {
    (async () => {
      try {
        await printPromotion();
        setPromotionLoading(false);
      } catch (error) {
        Honeybadger.notify(error, { message: 'Error getting promotion', context: { error } });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // annoying logic to show action buttons for the registry guidance variant OR when the registry banner has been dismissed
  const showActionButtons =
    !registryGuidanceExpLoading &&
    !promotionLoading &&
    (showRegistryGuidanceVariant || (printPromotionData && !printPromotionData.printPromotion));

  if (!pages) {
    return <div className={styles.leftContainer} />;
  }

  return (
    <PageCardUIProvider pages={pages} hasAnyRegistry={hasAnyRegistry}>
      <PageSectionHeader showActionButtons={showActionButtons} />
      <div className={styles.leftContainer}>
        {textGenerationModalOpen && (
          <TextGenerationModal
            isThinking={isThinking}
            generatedText={generatedText}
            retryRequest={isRetryableError ? retryRequest : undefined}
            title={generatedTextModalTitle}
            onClose={() => setModalOpen(false)}
            onPrimaryClick={textGenerationPrimaryClick}
            onSecondaryClick={textGenerationSecondaryClick}
            errorMessage={errorMessage}
            isInZeroState={isModalInZeroState}
          />
        )}

        <div>
          <ManagePagesRail />
          <PageItemsRail />
          <PageSettingsRail />
          <BasicInfoRail liteSite={liteSite} />
          {pages &&
            pages.map((page, index) => {
              const scrollToLocation = index === pages.length && scrollToView;
              const key = `page-${page.id}`;
              if (page.type === 'LiteSitePage') {
                return <LiteHomePageCard key={key} page={page} />;
              }

              if (page.type === 'WeddingPage') {
                return <WeddingPageCard key={key} page={page} />;
              }

              if (page.type === 'RegistryPage') {
                return hasAnyRegistry || !liteSite ? <RegistryPageCard key={key} page={page} /> : null;
              }

              return <PageCard key={key} page={page} liteSite={liteSite} scrollIntoView={scrollToLocation} />;
            })}
          {shouldShowDressCodeGuidance && <DressCodePageCard liteSite={liteSite} onCreatedPage={handleCreatedPage} />}
          {seeRsvpCard && <RsvpPageCard seeRsvpGuidance={seeRsvpGuidanceInPlace} />}
          {liteSite && (goToWorkflow || !!helpfulIdeasPages.length) && (
            <HelpfulIdeasSection pages={helpfulIdeasPages} goToWorkflow={goToWorkflow} />
          )}

          <AddPageButton
            block
            children={`Add a Custom ${pageOrSection}`}
            onClick={() => {
              track('Item Action Initiated', {
                action: 'add',
                page: 'add custom page',
              });
              setScrollToView(true);
              history.push(addPageRoute);
            }}
          />
        </div>
      </div>
    </PageCardUIProvider>
  );
};

export default LeftContainer;
